"use client"

import { useEffect } from "react"

import { AnalyticsEvent, track } from "@/lib/analytics"
import { CTA_SEEN_ATTR, createSeenOnce } from "@/lib/articles/cta-measure"

/**
 * Reports `cta_viewed` the first time each marked offer link on an article
 * (closing band, next-step rail, inline offer) is at least half on screen, once
 * per placement per page view. Renders nothing, so it cannot shift the layout.
 *
 * The page marks elements with `data-cta-seen`; this leaf only reads them. Only
 * the inline copy of the rail is marked (the side rail is on screen from load),
 * and it is hidden by CSS at 1024px and up, where it never intersects. A reader
 * past the meter sees the prose behind the wall; the wall replaces those nodes
 * after this has observed them, so the inline offer link reports nothing for
 * him, while the closing band and the rail, outside the wall, still do.
 */
export function ArticleCtaSeen({ slug }: { slug: string }) {
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return
    const marked = Array.from(document.querySelectorAll<HTMLElement>(`[${CTA_SEEN_ATTR}]`))
    if (marked.length === 0) return
    const once = createSeenOnce((placement) =>
      track(AnalyticsEvent.CtaViewed, { placement, article_slug: slug }),
    )
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          observer.unobserve(entry.target)
          once(entry.target.getAttribute(CTA_SEEN_ATTR))
        }
      },
      { threshold: 0.5 },
    )
    for (const el of marked) observer.observe(el)
    return () => observer.disconnect()
  }, [slug])

  return null
}
